/**
 * Membres de l'espace (CDC §8.1).
 *
 * L'invitation ne transmet PAS de mot de passe : elle produit un lien, et
 * l'invité choisit le sien. C'est plus sûr, et cela évite de recopier à la main
 * une chaîne où l'on confond `O` et `0` — ce qui a effectivement bloqué l'accès
 * à cet espace le 2026-09-07.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  api,
  type InvitationCreee,
  type Membre,
  type MembershipStatus,
  type RoleEspace,
} from '../api/client';
import { Champ, ErreurFormulaire, Ligne, Panneau } from '../composants/Formulaire';
import { avertissementSansNom, changementsMembre, saisieDepuis, type SaisieMembre } from '../domaine/membres';

const STATUT: Record<MembershipStatus, { texte: string; classe: string }> = {
  ACTIVE: { texte: 'Actif', classe: 'actif' },
  INVITED: { texte: 'Invité', classe: 'attente' },
  SUSPENDED: { texte: 'Suspendu', classe: 'suspendu' },
  REVOKED: { texte: 'Révoqué', classe: 'revoque' },
};

export function UsersPage() {
  const [membres, setMembres] = useState<Membre[] | null>(null);
  const [roles, setRoles] = useState<RoleEspace[]>([]);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [invitation, setInvitation] = useState<InvitationCreee | null>(null);
  const [enEdition, setEnEdition] = useState<Membre | null>(null);

  const charger = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([api.members(), api.roles()]);
      setMembres(m);
      setRoles(r);
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e : new Error('Chargement impossible.'));
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const agir = async (action: () => Promise<unknown>, message: string) => {
    setErreur(null);
    setSucces(null);
    try {
      await action();
      setSucces(message);
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e : new Error('Action impossible.'));
    }
  };

  const revoquer = (m: Membre) => {
    const motif = window.prompt(
      `Révoquer ${m.email} ?\n\n` +
        'Ses sessions ouvertes tombent immédiatement et il perd tout accès à cet espace. ' +
        'Motif (facultatif) :',
    );
    if (motif === null) return;
    void agir(() => api.revokeMember(m.membershipId, motif || undefined), 'Membre révoqué.');
  };

  const reinitialiserMfa = (m: Membre) => {
    const confirme = window.confirm(
      `Réinitialiser la double authentification de ${m.email} ?\n\n` +
        'Son secret est effacé, ses appareils de confiance révoqués, et ses sessions ' +
        'ouvertes fermées. Il devra configurer une nouvelle application ' +
        'd’authentification à sa prochaine connexion.',
    );
    if (!confirme) return;
    void agir(() => api.resetMemberMfa(m.membershipId), 'Double authentification réinitialisée.');
  };

  return (
    <>
      <div className="entete-page">
        <div>
          <h1>Membres</h1>
          <p>
            Qui peut se connecter à cet espace, et avec quels droits. L’invité choisit lui-même son
            mot de passe.
          </p>
        </div>
        <button onClick={() => setFormulaireOuvert(true)}>Inviter un membre</button>
      </div>

      <ErreurFormulaire
        message={erreur?.message ?? null}
        details={erreur instanceof ApiError ? erreur.details : undefined}
      />
      {succes && <div className="message succes">{succes}</div>}

      {invitation && <LienInvitation invitation={invitation} onFermer={() => setInvitation(null)} />}

      <div className="carte">
        {membres === null ? (
          <div className="vide">Chargement…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Membre</th>
                <th>Rôles</th>
                <th style={{ width: 120 }}>État</th>
                <th style={{ width: 110 }}>MFA</th>
                <th style={{ width: 210 }}></th>
              </tr>
            </thead>
            <tbody>
              {membres.map((m) => {
                const etat = STATUT[m.status];
                const nom = [m.firstName, m.lastName].filter(Boolean).join(' ');
                return (
                  <tr key={m.membershipId}>
                    <td>
                      <strong>{nom || m.email}</strong>
                      {nom && <div className="aide">{m.email}</div>}
                      {m.jobTitle && <div className="aide">{m.jobTitle}</div>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {m.roles.map((r) => (
                          <span key={r.id} className="etat suspendu">
                            {r.name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`etat ${etat.classe}`}>{etat.texte}</span>
                      {m.status === 'INVITED' && !m.invitationEnCours && (
                        <div className="aide">lien expiré</div>
                      )}
                    </td>
                    <td>
                      {m.mfaEnabled ? (
                        <span className="etat actif">Active</span>
                      ) : (
                        <span className="aide">—</span>
                      )}
                    </td>
                    <td className="actions">
                      {m.status !== 'REVOKED' && (
                        <button className="secondaire discret" onClick={() => setEnEdition(m)}>
                          Modifier
                        </button>
                      )}
                      {m.status === 'ACTIVE' && (
                        <button
                          className="secondaire discret"
                          onClick={() =>
                            void agir(
                              () => api.updateMember(m.membershipId, { status: 'SUSPENDED' }),
                              'Membre suspendu.',
                            )
                          }
                        >
                          Suspendre
                        </button>
                      )}
                      {m.status === 'SUSPENDED' && (
                        <button
                          className="secondaire discret"
                          onClick={() =>
                            void agir(
                              () => api.updateMember(m.membershipId, { status: 'ACTIVE' }),
                              'Membre réactivé.',
                            )
                          }
                        >
                          Réactiver
                        </button>
                      )}
                      {m.mfaEnabled && m.status !== 'REVOKED' && (
                        <button
                          className="secondaire discret danger-discret"
                          onClick={() => reinitialiserMfa(m)}
                        >
                          Réinitialiser MFA
                        </button>
                      )}
                      {m.status !== 'REVOKED' && (
                        <button
                          className="secondaire discret danger-discret"
                          onClick={() => revoquer(m)}
                        >
                          Révoquer
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="carte">
        <h2>Rôles disponibles</h2>
        <table>
          <thead>
            <tr>
              <th style={{ width: 180 }}>Rôle</th>
              <th>Ce qu’il permet</th>
              <th style={{ width: 130 }}>Double auth.</th>
              <th style={{ width: 100 }}>Membres</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.id}>
                <td>
                  <strong>{r.name}</strong>
                </td>
                <td className="aide" style={{ margin: 0 }}>
                  {r.description ?? '—'}
                </td>
                <td>
                  {r.mfaObligatoire ? (
                    <span className="etat attente">Obligatoire</span>
                  ) : (
                    <span className="aide">facultative</span>
                  )}
                </td>
                <td>{r.membres}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {formulaireOuvert && (
        <FormulaireInvitation
          roles={roles}
          onFermer={() => setFormulaireOuvert(false)}
          onInvite={(inv) => {
            setInvitation(inv);
            void charger();
          }}
        />
      )}

      {enEdition && (
        <FormulaireMembre
          membre={enEdition}
          onFermer={() => setEnEdition(null)}
          onEnregistre={async () => {
            setEnEdition(null);
            await charger();
            setSucces('Membre modifié.');
          }}
        />
      )}
    </>
  );
}

/** Corriger le prénom, le nom et l'intitulé de poste d'un membre. */
function FormulaireMembre({
  membre,
  onFermer,
  onEnregistre,
}: {
  membre: Membre;
  onFermer: () => void;
  onEnregistre: () => Promise<void>;
}) {
  const [saisie, setSaisie] = useState<SaisieMembre>(() => saisieDepuis(membre));
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);
  const avertissement = avertissementSansNom(saisie);

  const enregistrer = async (e: FormEvent) => {
    e.preventDefault();
    const corps = changementsMembre(membre, saisie);
    if (corps === null) {
      onFermer();
      return;
    }
    setEnCours(true);
    setErreur(null);
    try {
      await api.updateMember(membre.membershipId, corps);
      await onEnregistre();
    } catch (e) {
      setErreur(e instanceof ApiError ? e : new Error('Enregistrement impossible.'));
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre="Modifier le membre"
      sousTitre={membre.email}
      onFermer={onFermer}
      actions={
        <>
          <button className="secondaire" onClick={onFermer} disabled={enCours}>
            Annuler
          </button>
          <button onClick={(e) => void enregistrer(e)} disabled={enCours}>
            {enCours ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => void enregistrer(e)}>
        <ErreurFormulaire
          message={erreur?.message ?? null}
          details={erreur instanceof ApiError ? erreur.details : undefined}
        />
        <Ligne>
          <Champ libelle="Prénom">
            <input
              type="text"
              autoFocus
              value={saisie.firstName}
              onChange={(e) => setSaisie((s) => ({ ...s, firstName: e.target.value }))}
            />
          </Champ>
          <Champ libelle="Nom">
            <input
              type="text"
              value={saisie.lastName}
              onChange={(e) => setSaisie((s) => ({ ...s, lastName: e.target.value }))}
            />
          </Champ>
        </Ligne>
        <p className="aide">
          Le nom est celui du compte : il ne peut pas être changé d’ici si la personne est aussi membre
          d’un autre espace.
        </p>
        {avertissement && <div className="message info">{avertissement}</div>}
        <Champ libelle="Intitulé de poste" aide="Indicatif : n’accorde aucun droit.">
          <input
            type="text"
            value={saisie.jobTitle}
            onChange={(e) => setSaisie((s) => ({ ...s, jobTitle: e.target.value }))}
          />
        </Champ>
      </form>
    </Panneau>
  );
}

/**
 * Le lien d'invitation, affiché UNE SEULE FOIS.
 *
 * Le jeton n'est pas stocké en clair : ni l'application ni la base ne peuvent le
 * réafficher. Le dire explicitement évite qu'on ferme la fenêtre en pensant le
 * retrouver plus tard.
 */
function LienInvitation({
  invitation,
  onFermer,
}: {
  invitation: InvitationCreee;
  onFermer: () => void;
}) {
  const [copie, setCopie] = useState(false);
  const lien = `${window.location.origin}/invitation/${invitation.token}`;
  const expire = new Date(invitation.expiresAt).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const copier = () => {
    void navigator.clipboard
      .writeText(lien)
      .then(() => setCopie(true))
      .catch(() => setCopie(false));
  };

  return (
    <div className="carte carte-invitation">
      <h2>Lien d’invitation pour {invitation.membre.email}</h2>
      <div className="carte-corps">
        <div className="message info">
          <strong>Copiez ce lien maintenant.</strong> Il n’est pas conservé et ne pourra pas être
          réaffiché — s’il est perdu, il faudra réinviter la personne. Valable jusqu’au {expire}.
        </div>

        <div className="saisie-ligne">
          <input type="text" readOnly value={lien} onFocus={(e) => e.currentTarget.select()} />
          <button onClick={copier}>{copie ? 'Copié' : 'Copier'}</button>
          <button className="secondaire" onClick={onFermer}>
            Fermer
          </button>
        </div>

        <p className="aide">
          Transmettez-le par le moyen de votre choix. La personne choisira son mot de passe
          elle-même ; vous ne le connaîtrez jamais.
        </p>
      </div>
    </div>
  );
}

/** Invitation d'un nouveau membre. */
function FormulaireInvitation({
  roles,
  onFermer,
  onInvite,
}: {
  roles: RoleEspace[];
  onFermer: () => void;
  onInvite: (invitation: InvitationCreee) => void;
}) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);

  const basculer = (id: string) =>
    setRoleIds((r) => (r.includes(id) ? r.filter((x) => x !== id) : [...r, id]));

  const mfaAnnoncee = roles.some((r) => roleIds.includes(r.id) && r.mfaObligatoire);

  const inviter = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setErreur(new Error('L’adresse électronique est obligatoire.'));
      return;
    }
    if (roleIds.length === 0) {
      setErreur(new Error('Choisissez au moins un rôle : sans rôle, la personne ne pourra rien faire.'));
      return;
    }

    setEnCours(true);
    setErreur(null);
    try {
      const resultat = await api.invite({
        email: email.trim(),
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
        ...(jobTitle.trim() ? { jobTitle: jobTitle.trim() } : {}),
        roleIds,
      });
      onInvite(resultat);
      onFermer();
    } catch (e) {
      setErreur(e instanceof ApiError ? e : new Error('Invitation impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre="Inviter un membre"
      sousTitre="Un lien sera produit ; la personne choisira son mot de passe."
      onFermer={onFermer}
      actions={
        <>
          <button className="secondaire" onClick={onFermer} disabled={enCours}>
            Annuler
          </button>
          <button onClick={(e) => void inviter(e)} disabled={enCours}>
            {enCours ? 'Invitation…' : 'Créer le lien'}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => void inviter(e)}>
        <ErreurFormulaire
          message={erreur?.message ?? null}
          details={erreur instanceof ApiError ? erreur.details : undefined}
        />

        <Champ libelle="Adresse électronique" requis aide="Elle servira d’identifiant de connexion.">
          <input
            type="text"
            autoFocus
            placeholder="prenom.nom@entreprise.fr"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Champ>

        <Ligne>
          <Champ libelle="Prénom">
            <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </Champ>
          <Champ libelle="Nom">
            <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Champ>
        </Ligne>

        <Champ libelle="Intitulé de poste" aide="Indicatif : n’accorde aucun droit.">
          <input
            type="text"
            placeholder="Technicien frigoriste"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
          />
        </Champ>

        <Champ libelle="Rôles" requis aide="Ce sont eux qui accordent les droits. Ils se cumulent.">
          <div className="liste-roles">
            {roles.map((r) => (
              <label key={r.id} className="choix-role">
                <input
                  type="checkbox"
                  checked={roleIds.includes(r.id)}
                  onChange={() => basculer(r.id)}
                />
                <span>
                  <strong>{r.name}</strong>
                  {r.mfaObligatoire && <span className="etat attente"> double auth. </span>}
                  {r.description && <div className="aide">{r.description}</div>}
                </span>
              </label>
            ))}
          </div>
        </Champ>

        {mfaAnnoncee && (
          <div className="message info">
            Un des rôles choisis impose la <strong>double authentification</strong>. La personne
            recevra un QR code à scanner au moment d’accepter l’invitation.
          </div>
        )}
      </form>
    </Panneau>
  );
}
